/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';

import {
  buildApiErrorAnalytics,
  buildErrorTrackingAnalytics,
} from './ApiErrors.analytics';
import {
  ApiErrorParams,
  ErrorTrackingParams,
  ServiceErrorType,
} from './ApiErrors.meta';

import { resolveAnalyticsParams } from '../../util/utils';

const platform = Platform.OS;

export const apiErrorEvent = (params: ApiErrorParams) => {
  return resolveAnalyticsParams(buildApiErrorAnalytics(params), platform);
};

export const errorTrackingEvent = (params: ErrorTrackingParams) => {
  return resolveAnalyticsParams(buildErrorTrackingAnalytics(params), platform);
};

// Axios error codes that indicate a client-side network failure - the
// request never got a response (timeout, offline, DNS failure, connection
// refused, CORS block, or a canceled request). Node-only codes like
// ECONNREFUSED/ENOTFOUND don't apply here since this app never runs on
// axios's Node http adapter, only its browser/RN XHR-fetch adapter.
const NETWORK_ERROR_CODES = new Set([
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ERR_CANCELED',
]);

// Shared heuristic for classifying whatever a service-layer catch block
// receives, so every caller of errorTrackingEvent doesn't need to
// re-implement the same axios-error-shape sniffing. Based on the same
// conventions axios itself uses: a response means the server actually
// replied (with an error status); a request with no response means the
// request went out but nothing came back (network/timeout); neither means
// the error was thrown before a request was ever made (e.g. a missing
// filter/token check in the calling code).
export const classifyServiceError = (
  error: unknown,
): {
  errorType: ServiceErrorType;
  errorCode: string | number;
  errorDescription: string;
  errorMessage: string;
} => {
  const candidate = error as {
    status?: number;
    code?: string;
    message?: string;
    response?: {
      status?: number;
      statusText?: string;
      data?: { error?: { message?: string } };
    };
    request?: unknown;
  };

  const isNetworkError =
    (Boolean(candidate?.request) && !candidate?.response) ||
    (candidate?.code !== undefined && NETWORK_ERROR_CODES.has(candidate.code));

  return {
    errorType: isNetworkError ? 'network' : 'api',
    errorCode:
      candidate?.status ??
      candidate?.response?.status ??
      candidate?.code ??
      'UNKNOWN',
    errorDescription: candidate?.code ?? 'API Error',
    errorMessage:
      candidate?.response?.data?.error?.message ||
      candidate?.message ||
      'Unknown error',
  };
};
